/**
 * Fractional sort keys for a document's blocks — the JS twin of
 * src-tauri/src/sync/fractional.rs, same alphabet and the same algorithm.
 *
 * Keys are base-62 strings ('0'-'9' < 'A'-'Z' < 'a'-'z', which is also
 * their code-unit order, so JS `<`, SQLite `ORDER BY sort_key` and the web
 * cache all agree) read as fractions in (0, 1): "V" ~ 0.5. There is always a
 * key between two others, so pressing Enter a hundred times in the middle of
 * a page never runs out (the documents tree's padded integers would after
 * about twenty), and two people inserting at once never renumber anything.
 */
const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const BASE = DIGITS.length;

const toDigits = (key) => [...key].map((c) => Math.max(0, DIGITS.indexOf(c)));
const render = (digits) => digits.map((d) => DIGITS[d]).join('');

/** A key strictly between `a` and `b` (null = open end). */
export function keyBetween(a, b) {
  if (a != null && b != null && a >= b) return `${a}V`; // invalid order: degrade, the next reassignment heals it
  const av = a != null ? toDigits(a) : [];
  const bv = b != null ? toDigits(b) : null;
  const out = [];
  let bounded = bv;
  for (let i = 0; ; i += 1) {
    const da = av[i] ?? 0;
    const db = bounded ? bounded[i] ?? 0 : BASE;
    if (db > da + 1) {
      out.push(da + Math.floor((db - da) / 2));
      return render(out);
    }
    out.push(da);
    if (db === da + 1) bounded = null; // no room at this digit: only the lower bound constrains deeper ones
    if (i > av.length + (bv ? bv.length : 0) + 4) {
      out.push(BASE / 2);
      return render(out);
    }
  }
}

/**
 * `key` with one random digit appended when that still sorts below `upper`:
 * two people inserting at the same spot at once get different keys (equal
 * keys would fall back to block-id order, which neither of them saw).
 */
export function jittered(key, upper, random = Math.random) {
  const candidate = key + DIGITS[1 + Math.floor(random() * (BASE - 1))];
  return upper == null || candidate < upper ? candidate : key;
}

/** Indices (into `keyed`) of a longest strictly increasing run of keys. */
function lisIndices(keyed) {
  const tails = [];
  const prev = new Array(keyed.length).fill(-1);
  for (let i = 0; i < keyed.length; i += 1) {
    let lo = 0;
    let hi = tails.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (keyed[tails[mid]].key < keyed[i].key) lo = mid + 1;
      else hi = mid;
    }
    if (lo > 0) prev[i] = tails[lo - 1];
    tails[lo] = i;
  }
  const kept = new Set();
  for (let i = tails.length ? tails[tails.length - 1] : -1; i !== -1; i = prev[i]) kept.add(keyed[i].index);
  return kept;
}

/**
 * `incoming`: `[{ id, key }]` in the wanted order (`key` null for a new
 * item). Returns `Map<id, newKey>` for ONLY the items whose key must change:
 * the longest already-ordered run keeps its keys, moved and new items get keys
 * between their kept neighbours — so typing a new paragraph writes one key,
 * and dragging one block rewrites one key.
 */
export function reassignKeys(incoming) {
  const keyed = [];
  incoming.forEach((item, index) => {
    if (item.key != null) keyed.push({ index, key: item.key });
  });
  const kept = lisIndices(keyed);
  const changes = new Map();
  let prevKey = null;
  for (let i = 0; i < incoming.length; i += 1) {
    if (kept.has(i)) {
      prevKey = incoming[i].key;
      continue;
    }
    let nextKey = null;
    for (let j = i + 1; j < incoming.length; j += 1) {
      if (kept.has(j)) {
        nextKey = incoming[j].key;
        break;
      }
    }
    const key = jittered(keyBetween(prevKey, nextKey), nextKey);
    changes.set(incoming[i].id, key);
    prevKey = key;
  }
  return changes;
}
